import { Chip, SxProps, Theme } from "@mui/material";
import Flex from "./Flex";
import { CHART_COLORS, CHART_TYPOGRAPHY } from "./app/provider/patient/chart";

export type ChipSelectorItem = {
  id: string;
  label: string;
  /** Optional trailing count, e.g. "Active 7". */
  count?: number;
};

export type ChipSelectorProps = {
  items: ChipSelectorItem[];
  activeId?: string;
  onSelect: (id: string) => void;
  ariaLabel?: string;
  sx?: SxProps<Theme>;
};

/**
 * Horizontal chip selector — section jump links, list filters, quick toggles.
 * The caller owns which chip is active.
 *
 * @author Rajkumar Rathod
 */
const ChipSelector = ({
  items,
  activeId,
  onSelect,
  ariaLabel = "Options",
  sx,
}: ChipSelectorProps) => {
  return (
    <Flex
      component="nav"
      aria-label={ariaLabel}
      gap={1}
      sx={{
        overflowX: "auto",
        scrollbarWidth: "thin",
        py: 0.5,
        ...(sx as object),
      }}
    >
      {items.map((item) => {
        const isActive = item.id === activeId;

        return (
          <Chip
            key={item.id}
            label={
              item.count === undefined
                ? item.label
                : `${item.label} ${item.count}`
            }
            clickable
            aria-current={isActive ? "true" : undefined}
            onClick={() => onSelect(item.id)}
            sx={{
              ...CHART_TYPOGRAPHY.body5,
              fontFamily: CHART_TYPOGRAPHY.fontFamily,
              bgcolor: isActive
                ? CHART_COLORS.btnPrimaryBg
                : CHART_COLORS.surfaceWhite,
              color: isActive
                ? CHART_COLORS.surfaceWhite
                : CHART_COLORS.textDark,
              border: `1px solid ${isActive ? CHART_COLORS.btnPrimaryBg : CHART_COLORS.border}`,
              "&:hover": {
                bgcolor: isActive
                  ? CHART_COLORS.btnPrimaryHoverBg
                  : CHART_COLORS.secondaryHover,
              },
            }}
          />
        );
      })}
    </Flex>
  );
};

export default ChipSelector;
